import * as React from 'react';
import { useLocation } from 'react-router-dom';
import type { User } from 'firebase/auth';
import {
  signInWithGoogle,
  signInWithMicrosoft,
  signInWithEmail,
  signUpWithEmail,
  signOutUser,
  sendPasswordReset,
} from '@/services/authService';
import { getFirebaseAuth } from '@/services/firebase';
import { createLogger } from '@/services/loggingService';

const logger = createLogger('AuthContext');

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  signUpWithEmail: (email: string, password: string) => Promise<User>;
  signInWithGoogle: () => Promise<User>;
  signInWithMicrosoft: () => Promise<User>;
  resetPassword: (email: string) => Promise<void>;
  logOut: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

const AUTH_REQUIRED_PATHS = new Set([
  '/login',
  '/signup',
  '/checkout',
  '/checkout/success',
  '/checkout/canceled',
  '/pricing/checkout',
  '/pricing/success',
]);

const AUTH_REQUIRED_PREFIXES = ['/settings', '/pricing/checkout', '/admin'];

const shouldEnableAuthForPath = (pathname: string) => {
  if (AUTH_REQUIRED_PATHS.has(pathname)) {
    return true;
  }
  return AUTH_REQUIRED_PREFIXES.some(prefix => pathname.startsWith(prefix)) || pathname.startsWith('/checkout');
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const location = useLocation();
  const enableAuth = React.useMemo(() => shouldEnableAuthForPath(location.pathname), [location.pathname]);

  React.useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    if (!enableAuth) {
      setIsLoading(false);
      return () => {
        cancelled = true;
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }

    setIsLoading(true);

    const initialize = async () => {
      try {
        const [{ onAuthStateChanged }, authInstance] = await Promise.all([
          import('firebase/auth'),
          getFirebaseAuth(),
        ]);
        if (cancelled) {
          return;
        }
        unsubscribe = onAuthStateChanged(authInstance, nextUser => {
          setUser(nextUser);
          setIsLoading(false);
        });
      } catch (error) {
        logger.error('Failed to initialize Firebase Auth', { error: error instanceof Error ? error.message : String(error) });
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [enableAuth]);

  const handleSignInWithEmail = React.useCallback(async (email: string, password: string) => {
    const credentials = await signInWithEmail(email, password);
    return credentials.user;
  }, []);

  const handleSignUpWithEmail = React.useCallback(async (email: string, password: string) => {
    const credentials = await signUpWithEmail(email, password);
    return credentials.user;
  }, []);

  const handleSignInWithGoogle = React.useCallback(async () => {
    const credentials = await signInWithGoogle();
    return credentials.user;
  }, []);

  const handleSignInWithMicrosoft = React.useCallback(async () => {
    const credentials = await signInWithMicrosoft();
    return credentials.user;
  }, []);

  const handleResetPassword = React.useCallback(async (email: string) => {
    await sendPasswordReset(email);
  }, []);

  const logOut = React.useCallback(async () => {
    await signOutUser();
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      signInWithEmail: handleSignInWithEmail,
      signUpWithEmail: handleSignUpWithEmail,
      signInWithGoogle: handleSignInWithGoogle,
      signInWithMicrosoft: handleSignInWithMicrosoft,
      resetPassword: handleResetPassword,
      logOut,
    }),
    [user, isLoading, handleSignInWithEmail, handleSignUpWithEmail, handleSignInWithGoogle, handleSignInWithMicrosoft, handleResetPassword, logOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
